import React, { useEffect, useMemo, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import {
  faClose,
  faGlassWater,
  faLayerGroup,
  faPlus,
  faTags,
  faTrashCan,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { useTranslation } from "react-i18next"
import GetSpace from "../../../utils/space"
import Button from "../../../components/Button/Button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ListSelections } from "../../../components/ListSelections/ListSelections"
import { CountElements } from "../../../components/CountElements/CountElements"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { ApplyPromotionRequest } from "../../../hooks/Promotions/useApplyPromotions"
import { EPromotionApplyTargetType } from "../../../Enum/EPromotionApplyTargetType"
import { useGetMeals } from "../../../hooks/Meals/useGetMeals"
import { useGetDrinks } from "../../../hooks/Drinks/useGetDrinks"
import { useGetCombo } from "../../../hooks/Combo/useGetCombo"
import { comboMemory, drinksMemory, mealsMemory } from "../../../Objects/Memory/Memory"

interface PromotionApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  helpYoutubeLink?: string
  onClose: () => void
  onConfirm: (request: ApplyPromotionRequest) => Promise<void>
}

type OptionValue =
  | "all_products"
  | "selected_products"
  | "all_meals"
  | "selected_meals"
  | "all_drinks"
  | "selected_drinks"
  | "all_combos"
  | "selected_combos"

export const PromotionApplyPopup: React.FC<PromotionApplyPopupProps> = ({
  title,
  actionLabel,
  helpTitle,
  helpDescription,
  helpBullets,
  helpKey,
  helpYoutubeLink,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("promotion")

  const { handleGetMeals } = useGetMeals()

  const { handleGetDrinks } = useGetDrinks()

  const { handleGetCombo } = useGetCombo()

  const [targetType, setTargetType] = useState<OptionValue>("all_products")

  const [selectedMealIds, setSelectedMealIds] = useState<number[]>([])

  const [selectedDrinkIds, setSelectedDrinkIds] = useState<number[]>([])

  const [selectedComboIds, setSelectedComboIds] = useState<number[]>([])

  const [enableMealsSelection, setEnableMealsSelection] = useState(false)

  const [enableDrinksSelection, setEnableDrinksSelection] = useState(false)

  const [enableCombosSelection, setEnableCombosSelection] = useState(false)

  useEffect(() => {
    handleGetMeals(() => {})
    handleGetDrinks(() => {})
    handleGetCombo(() => {})
  }, [])

  const options = useMemo(
    () => [
      { label: t("apply_target_all_products"), value: "all_products" },
      { label: t("apply_target_selected_products"), value: "selected_products" },
      { label: t("apply_target_all_meals"), value: "all_meals" },
      { label: t("apply_target_selected_meals"), value: "selected_meals" },
      { label: t("apply_target_all_drinks"), value: "all_drinks" },
      { label: t("apply_target_selected_drinks"), value: "selected_drinks" },
      { label: t("apply_target_all_combos"), value: "all_combos" },
      { label: t("apply_target_selected_combos"), value: "selected_combos" },
    ],
    [t],
  )

  const handleChangeTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as OptionValue
    setTargetType(value)

    if (!["selected_products", "selected_meals"].includes(value)) {
      setSelectedMealIds([])
    }

    if (!["selected_products", "selected_drinks"].includes(value)) {
      setSelectedDrinkIds([])
    }

    if (!["selected_products", "selected_combos"].includes(value)) {
      setSelectedComboIds([])
    }
  }

  const getRequest = (): ApplyPromotionRequest => {
    switch (targetType) {
      case "selected_products":
        return {
          targetType: EPromotionApplyTargetType.SelectedProducts,
          mealIds: selectedMealIds,
          drinkIds: selectedDrinkIds,
          comboIds: selectedComboIds,
        }
      case "all_meals":
        return { targetType: EPromotionApplyTargetType.AllMeals, mealIds: [], drinkIds: [], comboIds: [] }
      case "selected_meals":
        return {
          targetType: EPromotionApplyTargetType.SelectedMeals,
          mealIds: selectedMealIds,
          drinkIds: [],
          comboIds: [],
        }
      case "all_drinks":
        return { targetType: EPromotionApplyTargetType.AllDrinks, mealIds: [], drinkIds: [], comboIds: [] }
      case "selected_drinks":
        return {
          targetType: EPromotionApplyTargetType.SelectedDrinks,
          mealIds: [],
          drinkIds: selectedDrinkIds,
          comboIds: [],
        }
      case "all_combos":
        return { targetType: EPromotionApplyTargetType.AllCombos, mealIds: [], drinkIds: [], comboIds: [] }
      case "selected_combos":
        return {
          targetType: EPromotionApplyTargetType.SelectedCombos,
          mealIds: [],
          drinkIds: [],
          comboIds: selectedComboIds,
        }
      default:
        return { targetType: EPromotionApplyTargetType.AllProducts, mealIds: [], drinkIds: [], comboIds: [] }
    }
  }

  const shouldSelectMeals = targetType === "selected_products" || targetType === "selected_meals"

  const shouldSelectDrinks = targetType === "selected_products" || targetType === "selected_drinks"

  const shouldSelectCombos = targetType === "selected_products" || targetType === "selected_combos"

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faTags}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey={helpKey}
                    title={helpTitle}
                    variant="global"
                    description={helpDescription}
                    bullets={helpBullets}
                    youtubeLink={helpYoutubeLink}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="p-2">
              <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                <GenericDropDown
                  legend={t("apply_target_legend")}
                  data={options}
                  initialState={targetType}
                  handlerFuncion={handleChangeTarget}
                  descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.select.applyTargetLegend"
                />
              </div>

              {shouldSelectMeals && (
                <SelectionSection
                  title={t("apply_select_meals_action")}
                  countTitle={t("apply_selected_meals")}
                  emptyLabel={t("apply_no_selected_meals")}
                  selectedIds={selectedMealIds}
                  icon={faUtensils}
                  onAdd={() => setEnableMealsSelection(true)}
                  onRemove={(id) => setSelectedMealIds((prev) => prev.filter((itemId) => itemId !== id))}
                  addDescriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.addMeal"
                  removeDescriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.removeMeal"
                  getLabel={(id) => mealsMemory.findById(id)?.genericFoodEntity.name ?? "-"}
                />
              )}

              {shouldSelectDrinks && (
                <SelectionSection
                  title={t("apply_select_drinks_action")}
                  countTitle={t("apply_selected_drinks")}
                  emptyLabel={t("apply_no_selected_drinks")}
                  selectedIds={selectedDrinkIds}
                  icon={faGlassWater}
                  onAdd={() => setEnableDrinksSelection(true)}
                  onRemove={(id) => setSelectedDrinkIds((prev) => prev.filter((itemId) => itemId !== id))}
                  addDescriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.addDrink"
                  removeDescriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.removeDrink"
                  getLabel={(id) => drinksMemory.findById(id)?.genericFoodEntity.name ?? "-"}
                />
              )}

              {shouldSelectCombos && (
                <SelectionSection
                  title={t("apply_select_combos_action")}
                  countTitle={t("apply_selected_combos")}
                  emptyLabel={t("apply_no_selected_combos")}
                  selectedIds={selectedComboIds}
                  icon={faLayerGroup}
                  onAdd={() => setEnableCombosSelection(true)}
                  onRemove={(id) => setSelectedComboIds((prev) => prev.filter((itemId) => itemId !== id))}
                  addDescriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.addCombo"
                  removeDescriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.removeCombo"
                  getLabel={(id) => comboMemory.findById(id)?.genericFoodEntity.name ?? "-"}
                />
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={actionLabel}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                onClick={() => onConfirm(getRequest())}
                descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionApplyPopup.button.actionLabel"
              />
            </div>
          </div>
        </Animate>
      </div>

      {enableMealsSelection && (
        <ListSelections
          title={t("apply_target_selected_meals")}
          icon={faUtensils}
          listCards={mealsMemory.getListCards(true)}
          messageCount={t("list:label_meals_page_message_count")}
          messageEmpty={t("list:label_meals_page_message_empty")}
          onClinck={(id) => {
            setSelectedMealIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
            setEnableMealsSelection(false)
          }}
          onClonse={() => setEnableMealsSelection(false)}
        />
      )}

      {enableDrinksSelection && (
        <ListSelections
          title={t("apply_target_selected_drinks")}
          icon={faGlassWater}
          listCards={drinksMemory.getListCards(true)}
          messageCount={t("list:label_drinks_page_message_count")}
          messageEmpty={t("list:label_drinks_page_message_empty")}
          onClinck={(id) => {
            setSelectedDrinkIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
            setEnableDrinksSelection(false)
          }}
          onClonse={() => setEnableDrinksSelection(false)}
        />
      )}

      {enableCombosSelection && (
        <ListSelections
          title={t("apply_target_selected_combos")}
          icon={faLayerGroup}
          listCards={comboMemory.getListCards(true)}
          messageCount={t("list:label_combo_page_message_count")}
          messageEmpty={t("list:label_combo_page_message_empty")}
          onClinck={(id) => {
            setSelectedComboIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
            setEnableCombosSelection(false)
          }}
          onClonse={() => setEnableCombosSelection(false)}
        />
      )}
    </ModalPortal>
  )
}

const SelectionSection: React.FC<{
  title: string
  countTitle: string
  emptyLabel: string
  selectedIds: number[]
  icon: any
  onAdd: () => void
  onRemove: (id: number) => void
  addDescriptionKey: string
  removeDescriptionKey: string
  getLabel: (id: number) => string
}> = ({
  title,
  countTitle,
  emptyLabel,
  selectedIds,
  icon,
  onAdd,
  onRemove,
  addDescriptionKey,
  removeDescriptionKey,
  getLabel,
}) => {
  const { t } = useTranslation("promotion")

  return (
    <>
      <GetSpace top={10} />

      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-orange-100 px-2 py-1 text-orange-600">
              <FontAwesomeIcon icon={icon} />
            </span>

            <span className="text-sm font-semibold text-slate-700">{title}</span>
          </div>

          <ButtonIcon
            typeButton="button"
            type="primary"
            size="medium"
            icon={faPlus}
            sizeNumber={SIZE.buttonCloseIconSize}
            bgColor={THEME.colors.buttonBGPlusColor}
            iconColor={THEME.colors.buttonFGPlusColor}
            onClick={onAdd}
            descriptionKey={addDescriptionKey}
          />
        </div>

        <GetSpace top={5} />

        <CountElements count={selectedIds.length} title={countTitle} />

        <GetSpace top={5} />

        {selectedIds.length === 0 && <span className="text-xs text-slate-500">{emptyLabel}</span>}

        {selectedIds.map((id) => (
          <Animate key={id} type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700" title={getLabel(id)}>
                {getLabel(id)}
              </span>

              <button
                className="rounded-full bg-red-50 p-1.5 transition-colors hover:bg-red-100"
                type="button"
                onClick={() => onRemove(id)}
                title={t("buttons:button_remove")}
                data-interaction-description-key={removeDescriptionKey}
              >
                <FontAwesomeIcon icon={faTrashCan} color={THEME.colors.buttonBGRemoveColor} />
              </button>
            </div>
          </Animate>
        ))}
      </div>
    </>
  )
}
